import React, { useState } from 'react';
import { Firestore } from 'firebase/firestore';
import { Wand2, Plus, AlertCircle } from 'lucide-react';
import { getPatternPreview, PatternStep, PatternConfig, PatternRule, PromptOverride } from 'services/apiService';
import RuleEditor from './RuleEditor';
import type { AdditionalPictureOverride } from './StepEditor';

interface PatternBuilderProps {
  selectedColumns: string[];
  patternRules: PatternRule[];
  onPatternRulesChange: (rules: PatternRule[]) => void;
  patternPreviews: Record<string, PatternStep[]>;
  onPatternPreviewsChange: (previews: Record<string, PatternStep[]>) => void;
  treatmentName: string;
  onNameChange: (name: string) => void;
  imageColumn: string | null;
  onNext: () => void;
  projectId: string;
  db: Firestore;
  uid: string;
}

const PatternBuilder: React.FC<PatternBuilderProps> = ({
  selectedColumns,
  patternRules,
  onPatternRulesChange,
  patternPreviews,
  onPatternPreviewsChange,
  treatmentName,
  onNameChange,
  imageColumn,
  onNext,
  projectId,
  db,
  uid
}) => {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [loadingPatterns, setLoadingPatterns] = useState<Set<string>>(new Set());
  const [patternErrors, setPatternErrors] = useState<Record<string, string>>({});

  const addRule = () => {
    const newRule: PatternRule = {
      id: `rule-${Date.now()}`,
      condition: {
        column: selectedColumns[0] || '',
        operator: '=',
        value: ''
      },
      pattern_config: {
        pattern_project_id: projectId,
        pattern_thread_id: '',
        from_picture_id: '',
        to_picture_id: '',
        prompt_overrides: {},
        additionnal_pictures: []
      }
    };
    onPatternRulesChange([...patternRules, newRule]);
    setExpandedRules(prev => new Set([...prev, newRule.id]));
  };

  const updateRule = (id: string, updates: Partial<PatternRule>) => {
    onPatternRulesChange(
      patternRules.map(rule =>
        rule.id === id ? { ...rule, ...updates } : rule
      )
    );
  };

  const updatePatternConfig = (ruleId: string, updates: Partial<PatternConfig>) => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (rule) {
      updateRule(ruleId, {
        pattern_config: { ...rule.pattern_config, ...updates }
      });
    }
  };

  const deleteRule = (id: string) => {
    onPatternRulesChange(patternRules.filter(rule => rule.id !== id));
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const duplicateRule = (rule: PatternRule) => {
    const newRule: PatternRule = {
      ...JSON.parse(JSON.stringify(rule)),
      id: `rule-${Date.now()}`,
    };
    onPatternRulesChange([...patternRules, newRule]);
    setExpandedRules(prev => new Set([...prev, newRule.id]));
  };

  const toggleRuleExpanded = (id: string) => {
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const loadPatternPreview = async (ruleId: string, config: PatternConfig) => {
    if (!config.pattern_project_id || !config.pattern_thread_id ||
        !config.from_picture_id || !config.to_picture_id) {
      return;
    }

    setLoadingPatterns(prev => new Set([...prev, ruleId]));
    setPatternErrors(prev => ({ ...prev, [ruleId]: '' }));

    try {
      const preview = await getPatternPreview(
        config.pattern_project_id,
        config.pattern_thread_id,
        config.from_picture_id,
        config.to_picture_id
      );
      onPatternPreviewsChange({ ...patternPreviews, [ruleId]: preview.steps });
    } catch (err) {
      setPatternErrors(prev => ({
        ...prev,
        [ruleId]: err instanceof Error ? err.message : 'Failed to load pattern'
      }));
    } finally {
      setLoadingPatterns(prev => {
        const newSet = new Set(prev);
        newSet.delete(ruleId);
        return newSet;
      });
    }
  };

  const addPromptOverride = (ruleId: string, stepIndex: number) => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (!rule) return;

    const overrides = rule.pattern_config.prompt_overrides || {};
    const stepKey = String(stepIndex);
    const existingOverrides = overrides[stepKey] || [];

    updatePatternConfig(ruleId, {
      prompt_overrides: {
        ...overrides,
        [stepKey]: [...existingOverrides, { find: '', replace: '' }]
      }
    });
  };

  const updatePromptOverride = (
    ruleId: string,
    stepIndex: number,
    overrideIndex: number,
    updates: Partial<PromptOverride>
  ) => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (!rule) return;

    const overrides = rule.pattern_config.prompt_overrides || {};
    const stepKey = String(stepIndex);
    const stepOverrides = [...(overrides[stepKey] || [])];
    stepOverrides[overrideIndex] = { ...stepOverrides[overrideIndex], ...updates };

    updatePatternConfig(ruleId, {
      prompt_overrides: {
        ...overrides,
        [stepKey]: stepOverrides
      }
    });
  };

  const removePromptOverride = (ruleId: string, stepIndex: number, overrideIndex: number) => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (!rule) return;

    const overrides = rule.pattern_config.prompt_overrides || {};
    const stepKey = String(stepIndex);
    const stepOverrides = [...(overrides[stepKey] || [])];
    stepOverrides.splice(overrideIndex, 1);

    updatePatternConfig(ruleId, {
      prompt_overrides: {
        ...overrides,
        [stepKey]: stepOverrides
      }
    });
  };

  const updateAdditionalPictureOverride = (
    ruleId: string,
    stepIndex: number,
    picIndex: number,
    override: AdditionalPictureOverride
  ) => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (!rule) return;

    const currentPictures = [...(rule.pattern_config.additionnal_pictures || [])];

    // Find existing override for this step/picture or create new one
    const existingIndex = currentPictures.findIndex(
      p => p.category === `step_${stepIndex}_pic_${picIndex}`
    );

    const newPictureConfig = {
      category: `step_${stepIndex}_pic_${picIndex}`,
      source: override.source,
      column_name: override.column_name,
      fixed_url: override.fixed_url,
      // Store original info for reference
      _original_id: override.original_id,
      _original_category: override.original_category
    };

    if (existingIndex >= 0) {
      currentPictures[existingIndex] = newPictureConfig;
    } else if (override.column_name || override.fixed_url) {
      // Only add if there's actually an override value
      currentPictures.push(newPictureConfig);
    }

    updatePatternConfig(ruleId, { additionnal_pictures: currentPictures });
  };

  const getAdditionalPictureOverrides = (ruleId: string, stepIndex: number): AdditionalPictureOverride[] => {
    const rule = patternRules.find(r => r.id === ruleId);
    if (!rule) return [];

    return (rule.pattern_config.additionnal_pictures || [])
      .filter(p => p.category?.startsWith(`step_${stepIndex}_`))
      .map(p => ({
        original_id: (p as any)._original_id || '',
        original_category: (p as any)._original_category || p.category,
        source: p.source,
        column_name: p.column_name,
        fixed_url: p.fixed_url
      }));
  };

  const hasValidPatternRules = patternRules.length > 0 &&
    patternRules.every(r =>
      r.condition.value.trim() !== '' &&
      r.pattern_config.pattern_project_id.trim() !== '' &&
      r.pattern_config.pattern_thread_id.trim() !== '' &&
      r.pattern_config.from_picture_id.trim() !== '' &&
      r.pattern_config.to_picture_id.trim() !== ''
    );

  const canProceed = treatmentName.trim() !== '' &&
    imageColumn !== null &&
    hasValidPatternRules;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Wand2 className="w-6 h-6 text-purple-400" />
        <h2 className="text-xl font-semibold text-white">Build Pattern Rules</h2>
      </div>

      <p className="text-sm text-gray-400">
        Create rules that apply different patterns based on your data. Patterns are sequences of image operations
        that can be replayed on new images with customizable prompts using your BQ columns.
      </p>

      {/* Treatment Name */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Treatment Name
        </label>
        <input
          type="text"
          value={treatmentName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g., Product Background Replacement"
          className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition"
        />
      </div>

      {/* Available Variables */}
      {selectedColumns.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Available Variables from BQ (use in prompt replacements)
          </label>
          <div className="flex flex-wrap gap-2">
            {selectedColumns.map((column) => (
              <span
                key={column}
                className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-lg text-sm font-mono text-purple-300"
              >
                {`{${column}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pattern Rules */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Pattern Rules</h3>
          <button
            onClick={addRule}
            disabled={selectedColumns.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>

        {patternRules.length === 0 ? (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400 mb-4">No pattern rules defined yet</p>
            <button
              onClick={addRule}
              disabled={selectedColumns.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add Your First Pattern Rule
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {patternRules.map((rule, index) => (
              <RuleEditor
                key={rule.id}
                rule={rule}
                index={index}
                selectedColumns={selectedColumns}
                isExpanded={expandedRules.has(rule.id)}
                patternPreview={patternPreviews[rule.id]}
                projectId={projectId}
                db={db}
                uid={uid}
                isLoadingPattern={loadingPatterns.has(rule.id)}
                patternError={patternErrors[rule.id]}
                onToggleExpanded={() => toggleRuleExpanded(rule.id)}
                onUpdateRule={(updates) => updateRule(rule.id, updates)}
                onUpdatePatternConfig={(updates) => updatePatternConfig(rule.id, updates)}
                onDeleteRule={() => deleteRule(rule.id)}
                onDuplicateRule={() => duplicateRule(rule)}
                onLoadPattern={() => loadPatternPreview(rule.id, rule.pattern_config)}
                onAddPromptOverride={(stepIndex) => addPromptOverride(rule.id, stepIndex)}
                onUpdatePromptOverride={(stepIndex, idx, updates) => updatePromptOverride(rule.id, stepIndex, idx, updates)}
                onRemovePromptOverride={(stepIndex, idx) => removePromptOverride(rule.id, stepIndex, idx)}
                onUpdateAdditionalPictureOverride={(stepIndex, picIdx, override) =>
                  updateAdditionalPictureOverride(rule.id, stepIndex, picIdx, override)
                }
                getAdditionalPictureOverrides={(stepIndex) => getAdditionalPictureOverrides(rule.id, stepIndex)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Warnings */}
      {selectedColumns.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-300 font-medium">No columns selected</p>
            <p className="text-xs text-yellow-400 mt-1">
              Please select at least one column above to create conditional rules.
            </p>
          </div>
        </div>
      )}

      {/* Example */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">How it works</h3>
        <div className="space-y-2 text-sm text-gray-400">
          <p>1. Define a condition to match rows (e.g., product_type = "Phone")</p>
          <p>2. Select a thread and pick the start/end pictures to define your pattern</p>
          <p>3. Load the pattern steps to see all operation details</p>
          <p>4. Customize prompts by adding find/replace rules with BQ column variables</p>
          <p>5. Override reference images with values from BQ columns</p>
          <p className="text-xs text-gray-500 mt-2">
            Example: Replace "blue background" &rarr; {`"{background_color}"`} background to dynamically change colors
          </p>
        </div>
      </div>

      {/* Next Button */}
      <button
        onClick={onNext}
        disabled={!canProceed}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
      >
        Preview Treatment
        <span className="text-xs opacity-75">
          ({patternRules.length} pattern rule{patternRules.length !== 1 ? 's' : ''})
        </span>
      </button>
    </div>
  );
};

export default PatternBuilder;
